import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppState } from '../../app/state';
import { callAction } from '../../app/actions';
import { ApiError } from '../../api/client';
import { bytesText, whenText } from '../../lib/format';
import { TypedConfirm } from '../../components/TypedConfirm';
import { Fieldset, PasswordField } from './fields';
import {
  backupDownloadUrl,
  deleteBackup,
  dismissInterrupted,
  fetchBackupJob,
  fetchBackups,
  startExport,
  startImport,
  uploadBackup,
  type BackupCompat,
  type BackupEntry,
  type BackupJob,
  type BackupsPayload,
} from '../../api/backups';

/**
 * BACKUPS — export this whole installation as one file, and restore one over it.
 *
 * The fourth tab of the admin page, drawn only for `BACKUP_ADMIN`. It follows the API-keys
 * tab's shape deliberately (`.role-split` master–detail, its own ids because `#role-list`
 * means the Roles tab to a spec) and its polling discipline is `AiTab`'s: one interval in a
 * ref, one `stopPolling`, cleared from every exit including unmount.
 *
 * NO TOPBAR BUTTON. A standalone role would naturally want its own control up there, and it
 * does not get one: six of the nine pixel baselines photograph the top bar, and the About
 * panel's history records that a change of that size once slipped UNDER the 1% threshold — so
 * a new button there is a silent baseline drift as much as it is a UI change nobody asked for.
 * `#permmodal` is photographed nowhere, which is where this lives.
 *
 * ── WHAT THIS SCREEN IS FOR, AND WHY IT SAYS SO MUCH ──────────────────────────────────────
 * Restoring replaces every account, document, revision and image in the installation. That is
 * not a thing to discover afterwards, so the screen is verbose exactly where it matters:
 *
 *   - the list says what each bundle IS — made by which version, when, by whom, how many rows,
 *     whether it can be restored here at all;
 *   - the restore dialog says what will be lost, that a safety copy is written first, and WHO
 *     WILL BE ABLE TO SIGN IN AFTERWARDS, by name;
 *   - when the current user is not among those names it says, in red, that they will be signed
 *     out and cannot sign back in with this account. That is the single most useful sentence
 *     here: it is the difference between restoring a colleague's bundle and locking yourself
 *     out of your own installation;
 *   - and the confirmation is the typed gesture the live-verification arm switch uses, because
 *     these are the two most consequential buttons in the application and they should ask in
 *     the same way.
 *
 * ── AFTER A RESTORE, THIS TAB RELOADS THE PAGE ────────────────────────────────────────────
 * Nothing less is honest. Every node, document, tag, role and user in memory came from a
 * database that no longer exists, and reconstructing them piecemeal is a worse version of what
 * boot already does properly. It goes through `callAction('reloadPage')` rather than
 * `location.reload()` because `Location` is unforgeable, and a spec needs somewhere to stand.
 */

const faint = { fontSize: 11.5, color: 'var(--ink-faint)' } as const;
const dim = { fontSize: 12, color: 'var(--ink-dim)' } as const;

const KIND_LABEL: Record<BackupEntry['kind'], string> = {
  export: 'Export',
  safety: 'Safety copy',
  uploaded: 'Uploaded',
};

/** Export is the ordinary one; a safety copy is machine-made; an upload came from elsewhere. */
const KIND_COLOR: Record<BackupEntry['kind'], string> = {
  export: 'var(--accent)',
  safety: 'var(--status-needs-update)',
  uploaded: 'var(--ink-dim)',
};

function compatText(entry: BackupEntry): string {
  const version = entry.summary?.appVersion ?? 'an unknown version';
  const rules: Record<BackupCompat, string> = {
    same: 'Made by this version of InfoStation — it restores exactly as it is.',
    'older-will-migrate': `Made by an older version (${version}). It can be restored: the data is brought up to date afterwards${entry.pending ? `, applying ${entry.pending} update${entry.pending === 1 ? '' : 's'}` : ''}.`,
    'newer-refused': `Made by a newer version (${version}) than this server runs. Update the server first, then restore it.`,
    'foreign-refused': 'This does not come from a compatible InfoStation installation, so it cannot be restored here.',
    unreadable: 'This file cannot be read as a backup.',
  };
  return rules[entry.compat];
}

const RESTORABLE: BackupCompat[] = ['same', 'older-will-migrate'];

/** A refusal's own sentence, never a status code. */
function failureText(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { message?: string; error?: string } | undefined;
    return body?.message || body?.error || err.message;
  }
  return err instanceof Error ? err.message : String(err);
}

export function BackupsTab() {
  const s = useAppState();
  const [payload, setPayload] = useState<BackupsPayload | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  /** Held as a NAME and resolved against the live list, the idiom the Roles tab established. */
  const [selectedName, setSelectedName] = useState<string | null>(null);
  const [job, setJob] = useState<BackupJob | null>(null);
  const [busyNote, setBusyNote] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [restoreDone, setRestoreDone] = useState(false);
  /** Held only while the form is on screen, and cleared the moment it is sent or abandoned. */
  const [exportPassphrase, setExportPassphrase] = useState('');
  const [restorePassphrase, setRestorePassphrase] = useState('');

  const fileInput = useRef<HTMLInputElement>(null);
  const poll = useRef<ReturnType<typeof setInterval> | null>(null);
  /** The ticket that keeps a restore's progress readable while the User table is being rebuilt. */
  const ticket = useRef<string | undefined>(undefined);

  const load = useCallback(async () => {
    try {
      const data = await fetchBackups();
      setPayload(data);
      setLoadError(null);
      // A job somebody started in another tab, or before this one was opened.
      if (data.job && data.job.state === 'running') setJob(data.job);
    } catch (err) {
      setLoadError(failureText(err));
    } finally {
      setLoaded(true);
    }
  }, []);

  const stopPolling = useCallback(() => {
    if (poll.current) clearInterval(poll.current);
    poll.current = null;
  }, []);

  // EVERY way out of this tab runs through here — the discipline AiTab's header insists on, so
  // "the screen went away and it polled for ever" cannot come back by adding another exit.
  useEffect(() => () => stopPolling(), [stopPolling]);

  useEffect(() => {
    void load();
  }, [load]);

  const followJob = useCallback(
    (id: string, kind: 'export' | 'import') => {
      stopPolling();
      const tick = async () => {
        let data: BackupJob;
        try {
          data = await fetchBackupJob(id, ticket.current);
        } catch {
          // A dropped poll is not a failed job — the next tick asks again. During a restore
          // this is the expected case for a beat or two.
          return;
        }
        setJob(data);
        if (data.state === 'running') return;
        stopPolling();
        ticket.current = undefined;
        if (data.state === 'done' && kind === 'import') {
          // The whole client is stale by definition now. Show the outcome for a moment so it
          // is readable, then start over.
          setRestoreDone(true);
          window.setTimeout(() => callAction('reloadPage'), 1500);
          return;
        }
        void load();
      };
      // Immediately, then on an interval: a first poll 1.5s later is 1.5s of a blank card.
      poll.current = setInterval(() => void tick(), 1500);
      void tick();
    },
    [load, stopPolling],
  );

  const entries = payload?.entries ?? [];
  const selected = entries.find((e) => e.name === selectedName) ?? entries[0] ?? null;
  const running = !!job && job.state === 'running';

  const onExport = async () => {
    setBusyNote(null);
    try {
      const { jobId } = await startExport(exportPassphrase || undefined);
      setExportPassphrase('');
      ticket.current = undefined;
      followJob(jobId, 'export');
    } catch (err) {
      setBusyNote(failureText(err));
    }
  };

  const onUpload = async (file: File) => {
    setBusyNote(null);
    try {
      const entry = await uploadBackup(file);
      await load();
      setSelectedName(entry.name);
    } catch (err) {
      setBusyNote(failureText(err));
    }
  };

  const onDelete = async (entry: BackupEntry) => {
    if (!confirm(`Delete ${entry.name}? This cannot be undone.`)) return;
    try {
      await deleteBackup(entry.name);
      if (selectedName === entry.name) setSelectedName(null);
      await load();
    } catch (err) {
      setBusyNote(failureText(err));
    }
  };

  const onRestore = async (entry: BackupEntry, typed: string) => {
    setDialogError(null);
    try {
      const { jobId, ticket: t } = entry.encrypted
        ? await startImport(entry.name, { passphrase: typed })
        : await startImport(entry.name, { confirmCode: typed });
      ticket.current = t;
      setRestorePassphrase('');
      setConfirming(false);
      followJob(jobId, 'import');
    } catch (err) {
      // The dialog STAYS OPEN on a refusal, with the server's own sentence in it. Closing it
      // would make the reader start the gesture again to find out what was wrong.
      setDialogError(failureText(err));
    }
  };

  const me = s.currentUser?.username ?? '';
  const admins = selected?.summary?.adminUsernames ?? [];
  const lockingMyselfOut = !!selected?.summary && !!me && !admins.includes(me);

  return (
    <>
      <div className="perm-section-lead">
        A backup is a copy of this whole installation in one file: every user, role and team,
        the map, every documentation page with its history, and the uploaded images. Download
        one to keep elsewhere, or upload one taken on another installation.{' '}
        <b>Restoring replaces everything that is here now.</b>
      </div>

      {payload?.interrupted ? (
        <div
          id="bk-interrupted"
          className="card"
          style={{ border: '1px solid var(--status-needs-update)', marginBottom: 10 }}
        >
          <h3>
            <span className="role-title">A restore did not finish</span>
          </h3>
          <div style={{ ...dim, padding: '0 10px 10px' }}>
            <div>{payload.interrupted.description}</div>
            {payload.interrupted.error ? (
              <div style={{ marginTop: 4, color: 'var(--status-doc-rejected)' }}>
                It stopped because: {payload.interrupted.error}
              </div>
            ) : null}
            <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
              {payload.interrupted.safetyCopy ? (
                <button
                  className="lbtn"
                  id="bk-interrupted-show"
                  onClick={() => setSelectedName(payload.interrupted!.safetyCopy)}
                >
                  Show the safety copy
                </button>
              ) : null}
              <button
                className="lbtn"
                id="bk-interrupted-dismiss"
                title="Hide this notice. The server stays in maintenance until a restore succeeds."
                onClick={async () => {
                  try {
                    await dismissInterrupted();
                    await load();
                  } catch (err) {
                    setBusyNote(failureText(err));
                  }
                }}
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {job ? (
        <div id="bk-job" className="card" style={{ marginBottom: 10 }}>
          <h3>
            <span className="role-title">
              {job.kind === 'import' ? 'Restoring a backup' : 'Exporting a backup'}
            </span>
            <span className="grow" />
            <span style={faint}>started {whenText(job.startedAt)}</span>
          </h3>
          <div style={{ ...dim, padding: '0 10px 10px' }}>
            {job.state === 'running' ? (
              <div id="bk-job-step">
                {job.step}
                {typeof job.detail?.table === 'string' ? ` — ${job.detail.table}` : ''}
                {typeof job.detail?.index === 'number' && typeof job.detail?.total === 'number'
                  ? ` (${job.detail.index} of ${job.detail.total})`
                  : ''}
                {typeof job.detail?.verifying === 'number' && typeof job.detail?.total === 'number'
                  ? ` (${job.detail.verifying} of ${job.detail.total})`
                  : ''}
              </div>
            ) : job.state === 'error' ? (
              <div id="bk-job-error" style={{ color: 'var(--status-doc-rejected)' }}>
                {job.error}
              </div>
            ) : job.kind === 'import' ? (
              <div id="bk-job-done">
                Restored.{' '}
                {job.resultName ? <>The state before it is in {job.resultName}. </> : null}
                {restoreDone ? 'Reloading…' : null}
                {lockingMyselfOut ? <b> You will be signed out.</b> : null}
              </div>
            ) : (
              <div id="bk-job-done">Exported {job.resultName}.</div>
            )}
          </div>
        </div>
      ) : null}

      {busyNote ? (
        <div id="bk-note" className="perm-section-lead" style={{ color: 'var(--status-doc-rejected)' }}>
          {busyNote}
        </div>
      ) : null}

      <div className="role-split">
        <div id="bk-list" className="role-list">
          <button id="bk-export" className="lbtn role-add" disabled={running} onClick={() => void onExport()}>
            <svg className="ico">
              <use href="#i-export" />
            </svg>
            Export now
          </button>
          <button
            id="bk-upload"
            className="lbtn role-add"
            disabled={running}
            onClick={() => fileInput.current?.click()}
          >
            <svg className="ico">
              <use href="#i-import" />
            </svg>
            Upload a backup…
          </button>
          <div style={{ margin: '2px 0 6px' }}>
            <PasswordField
              placeholder="Passphrase (optional)"
              value={exportPassphrase}
              onChange={setExportPassphrase}
            />
            <div style={{ ...faint, marginTop: 2 }}>
              A passphrase protects the exported file wherever it is kept. It cannot be recovered:
              a backup whose passphrase is lost cannot be restored.
            </div>
          </div>
          <input
            ref={fileInput}
            id="bk-upload-file"
            type="file"
            accept=".zip,.enc"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              // Cleared so choosing the SAME file twice fires change again — the avatar cell's
              // idiom, and the reason an upload that failed can simply be retried.
              e.target.value = '';
              if (file) void onUpload(file);
            }}
          />

          {!loaded ? (
            <div className="pick-empty">Loading…</div>
          ) : loadError ? (
            <div className="pick-empty">{loadError}</div>
          ) : entries.length === 0 ? (
            <div className="pick-empty">No backups yet — export one above.</div>
          ) : (
            entries.map((entry) => (
              <div
                key={entry.name}
                data-backup={entry.name}
                className={entry.name === selected?.name ? 'rolerow sel' : 'rolerow'}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    flex: '0 0 auto',
                    background: KIND_COLOR[entry.kind],
                  }}
                />
                <button className="rolerow-name" onClick={() => setSelectedName(entry.name)}>
                  <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {entry.name}
                  </span>
                  <span style={{ ...faint, display: 'block' }}>
                    {KIND_LABEL[entry.kind]} · {bytesText(entry.bytes)} · {whenText(entry.createdAt)}
                  </span>
                </button>
                {entry.encrypted ? (
                  <svg className="ico" style={{ width: 13, height: 13 }} aria-label="Protected by a passphrase">
                    <use href="#i-lock" />
                  </svg>
                ) : null}
                <button
                  className="role-bin"
                  title="Delete this backup"
                  disabled={running}
                  onClick={() => void onDelete(entry)}
                >
                  <svg className="ico">
                    <use href="#i-trash" />
                  </svg>
                </button>
              </div>
            ))
          )}
        </div>

        <div id="bk-detail" className="role-detail">
          {selected ? (
            <div id="bk-card" className="card">
              <h3>
                <span className="role-title">{selected.name}</span>
                <span className="grow" />
                <span style={faint}>{KIND_LABEL[selected.kind]}</span>
              </h3>

              <div className="perm-grp-body">
                <Fieldset title="What is inside">
                  {selected.summary ? (
                    <div style={dim}>
                      <div>
                        Made by InfoStation {selected.summary.appVersion} on{' '}
                        {whenText(selected.summary.exportedAt)}, by {selected.summary.exportedBy}.
                      </div>
                      <div>
                        {selected.summary.tables} tables, {selected.summary.rows.toLocaleString()} rows,{' '}
                        {selected.summary.uploadsFiles} uploaded files ({bytesText(selected.summary.uploadsBytes)}).
                      </div>
                      <div style={{ marginTop: 4 }}>
                        {admins.length ? (
                          <>
                            Able to sign in after a restore: <b>{admins.join(', ')}</b>.
                          </>
                        ) : (
                          <b>This backup contains no administrator account.</b>
                        )}
                      </div>
                      {selected.encKeyMatch === false ? (
                        <div style={{ marginTop: 4, color: 'var(--status-needs-update)' }}>
                          Made on a deployment with a different credential key: live-verification
                          passwords inside it cannot be read here, and those targets will be disarmed.
                        </div>
                      ) : null}
                    </div>
                  ) : selected.encrypted ? (
                    <div style={dim}>
                      Protected by a passphrase. What is inside — the version, the row counts and
                      which accounts can sign in afterwards — is checked when it is restored.
                    </div>
                  ) : (
                    <div style={dim}>{selected.problem ?? 'This backup could not be read.'}</div>
                  )}
                </Fieldset>

                <Fieldset title="Can it be restored here?">
                  <div id="bk-compat" style={dim}>
                    {selected.encrypted
                      ? 'Not known until it is opened: enter the passphrase to restore it, and it is checked then. An incompatible one is refused before anything is replaced.'
                      : compatText(selected)}
                  </div>
                </Fieldset>
              </div>

              <div className="role-foot">
                <a id="bk-download" className="lbtn" href={backupDownloadUrl(selected.name)} download>
                  <svg className="ico">
                    <use href="#i-export" />
                  </svg>
                  Download
                </a>
                {(RESTORABLE.includes(selected.compat) || selected.encrypted) && !confirming ? (
                  <button
                    id="bk-import"
                    className="pbtn"
                    disabled={running}
                    onClick={() => {
                      setDialogError(null);
                      setConfirming(true);
                    }}
                  >
                    Restore this backup…
                  </button>
                ) : null}
              </div>

              {confirming ? (
                <div
                  id="bk-import-dialog"
                  style={{
                    margin: '0 10px 10px',
                    padding: 10,
                    border: '1px solid var(--status-needs-update)',
                    borderRadius: 6,
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>
                    Restore “{selected.name}” over this installation
                  </div>
                  <ul style={{ ...dim, margin: '0 0 8px', paddingLeft: 18 }}>
                    <li>
                      Every table is replaced: users, roles, teams, the map, every documentation page
                      and its history, the settings and the uploaded images.
                    </li>
                    <li>Everything currently here is lost.</li>
                    <li>A safety copy of the current state is written first, and appears in this list.</li>
                    <li>
                      {admins.length ? (
                        <>
                          Afterwards these accounts can sign in: <b>{admins.join(', ')}</b>.
                        </>
                      ) : (
                        <b>This backup has no administrator account: nobody will be able to administer this installation afterwards.</b>
                      )}
                    </li>
                  </ul>
                  {lockingMyselfOut ? (
                    <div
                      id="bk-import-foreign"
                      style={{ color: 'var(--status-doc-rejected)', fontWeight: 600, marginBottom: 8 }}
                    >
                      You ({me}) are not among them: you will be signed out when the restore finishes,
                      and you cannot sign back in with this account.
                    </div>
                  ) : null}
                  {dialogError ? (
                    <div id="bk-import-error" style={{ color: 'var(--status-doc-rejected)', marginBottom: 8 }}>
                      {dialogError}
                    </div>
                  ) : null}
                  {selected.encrypted ? (
                    // A PROTECTED BUNDLE CONFIRMS ITSELF. Its code lives inside a manifest
                    // nobody can read without the passphrase, so there is nothing to type back
                    // — and producing the passphrase is a stronger statement of intent than
                    // retyping six characters that were on screen anyway.
                    <div id="bk-import-passphrase">
                      <div style={{ ...dim, marginBottom: 6 }}>
                        This backup is protected. Its passphrase is the confirmation, and what is
                        inside it is only checked once it opens.
                      </div>
                      <PasswordField
                        placeholder="Passphrase"
                        value={restorePassphrase}
                        onChange={setRestorePassphrase}
                      />
                      <div className="arm-row" style={{ marginTop: 8 }}>
                        <button
                          className="pbtn primary"
                          disabled={!restorePassphrase}
                          onClick={() => void onRestore(selected, restorePassphrase)}
                        >
                          Restore
                        </button>
                        <button
                          className="pbtn"
                          onClick={() => {
                            setConfirming(false);
                            setDialogError(null);
                            setRestorePassphrase('');
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <TypedConfirm
                      prompt={
                        <>
                          Type <b>{selected.code}</b> to confirm.
                        </>
                      }
                      expected={selected.code ?? ''}
                      actionLabel="Restore"
                      onConfirm={(typed) => onRestore(selected, typed)}
                      onCancel={() => {
                        setConfirming(false);
                        setDialogError(null);
                      }}
                    />
                  )}
                </div>
              ) : null}
            </div>
          ) : (
            <div className="pick-empty">{loaded ? 'No backup selected.' : 'Loading…'}</div>
          )}
        </div>
      </div>
    </>
  );
}
